/**
 * dashboard filter schema (IA §1.2 / §1.3, milestone v1.4 / Plan 02-04).
 *
 * Single source of truth for:
 *   - the shape of filter state held by `useUrlFilters` (Task 4.2);
 *   - the serialization/deserialization rules that keep the URL short;
 *   - the preset → [from, to] resolver used by both the frontend (to preview a
 *     range) and, conceptually, by the backend (`resolveTimeRange` in
 *     `src/dashboard/routes/api/schemas.ts`).
 *
 * Zod 4 is used here (same version v1 ships). `safeParse` returns
 * `{ success, data | error }` — same API as zod 3 for the subset we use.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Per-table sort state (Plan 03-05 Task 5.1). `column` is a free-form
 *  column key owned by the table; `dir` is asc/desc. Tables clear their
 *  sort (return to default) by deleting the entry from the `sort` map. */
export const TableSortSchema = z.object({
  column: z.string().min(1),
  dir: z.enum(['asc', 'desc']),
});

export type TableSort = z.infer<typeof TableSortSchema>;

/** Day-of-week filter for daily-granularity aggregations (replacing the old
 *  Bug A7 `includeWeekends` boolean). Three states:
 *    - 'all'       : no DOW predicate — every day of the week counted.
 *    - 'weekdays'  : Mon–Fri only (EXTRACT(DOW)=1..5). DEFAULT per product
 *                    decision ("по умолчанию исключить"): RMs don't routinely
 *                    work Sat/Sun so the weekend troughs in daily charts are
 *                    calendar noise, not churn.
 *    - 'weekends'  : Sat+Sun only (EXTRACT(DOW) IN (0,6)). For spot-checking
 *                    whether anyone was awake on the weekend.
 *  Wired through `dayFilter` in filtersQuerySchema and `weekendExclusionSQL`
 *  in services/analytics/filters.ts. */
export type DayFilter = 'all' | 'weekdays' | 'weekends';

/** Coerce legacy `includeWeekends=true/false` URL params to the new
 *  `dayFilter` vocabulary so old bookmarks keep rendering. Only consulted
 *  by the object-level preprocess below when `dayFilter` itself is absent.
 *    includeWeekends=true  → dayFilter='all'       (keep weekends)
 *    includeWeekends=false → dayFilter='weekdays'  (drop weekends — old default)
 */
function coerceLegacyIncludeWeekends(raw: unknown): DayFilter | undefined {
  if (raw === true || raw === 'true' || raw === '1') return 'all';
  if (raw === false || raw === 'false' || raw === '0') return 'weekdays';
  return undefined;
}

/** Zod schema matching every field on IA §1.2 (filter bar) + §1.3 (granularity). */
export const FilterSchema = z
  .preprocess(
    // Object-level preprocess — only purpose is to fold legacy
    // `includeWeekends` into `dayFilter` BEFORE the field-level parsers run,
    // so a URL carrying the old param (and no dayFilter) still hydrates into
    // a meaningful DayFilter value instead of dropping to the default. If
    // both are present, `dayFilter` wins (the newer key is authoritative).
    (raw) => {
      if (!raw || typeof raw !== 'object') return raw;
      const obj = raw as Record<string, unknown>;
      if (obj.dayFilter !== undefined) {
        // Drop the legacy key so it can't confuse downstream consumers.
        const { includeWeekends: _legacy, ...rest } = obj;
        return rest;
      }
      const coerced = coerceLegacyIncludeWeekends(obj.includeWeekends);
      if (coerced === undefined) return obj;
      const { includeWeekends: _legacy, ...rest } = obj;
      return { ...rest, dayFilter: coerced };
    },
    z.object({
      preset: z.enum(['24h', '7d', '30d', '90d', 'all', 'custom']).optional(),
      from: z.string().regex(ISO_DATE_RE).optional(),
      to: z.string().regex(ISO_DATE_RE).optional(),
      role: z.enum(['all', 'rm', 'non-rm']).default('all'),
      // BUG E-Cohort: `all` dropped from the enum. Cohort is purely a
      // segmentation filter — "no cohort filter" is the absence of the field.
      // `undefined` is the canonical "don't segment" value; the URL simply
      // omits `cohort` to convey it. Parsing legacy URLs / localStorage state
      // that still carries `cohort=all` rewrites to `undefined` via the
      // preprocessor so deep links degrade gracefully instead of 400-ing.
      cohort: z
        .preprocess(
          (v) => (v === 'all' || v === '' || v == null ? undefined : v),
          z.enum(['active', 'engaged', 'power']).optional(),
        ),
      // BUG G3 — `qs.parse(..., {comma: true})` returns a bare STRING for a
      // single-element CSV (e.g. `users=abn` → `'abn'`) but an ARRAY for two or
      // more (`users=abn,xyz` → `['abn','xyz']`). Without the preprocess below
      // a URL with exactly one selected user fails FilterSchema validation,
      // parseFromSearch falls back to DEFAULT_FILTERS, and the chip silently
      // disappears while the URL still carries `?users=abn`. Coerce string →
      // string[] here so the URL round-trip is robust regardless of how many
      // users are selected. Comma-split matches the `arrayFormat:'comma'`
      // serializer in stripDefaults. An empty string is treated as [] so
      // `users=` in the URL doesn't create a single `''` entry.
      users: z
        .preprocess(
          (v) => {
            if (typeof v === 'string') {
              return v.length === 0
                ? []
                : v
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean);
            }
            return v;
          },
          z.array(z.string()),
        )
        .default([]),
      // BUG G2 — `group_dm` removed from the UI (coach isn't used in group
      // DMs). Legacy URLs carrying `channel=group_dm` fall through to the
      // preprocess below, which rewrites unknown values back to `any` so old
      // shared links / localStorage state degrade gracefully instead of
      // throwing at `safeParse`.
      channel: z
        .preprocess(
          (v) => (v === 'group_dm' ? 'any' : v),
          z.enum(['any', 'dm', 'channel']),
        )
        .default('any'),
      /**
       * Which surface the turn arrived on (UI label: "Channel"). Named
       * `surface` because `channel` above is the Slack context type — two
       * different axes, and sharing a URL key would make one clobber the
       * other. Unknown strings coerce to the default so a hand-edited URL
       * degrades instead of blanking the whole filter bar.
       */
      surface: z
        .preprocess(
          (v) => (v === 'slack' || v === 'web' || v === 'all' ? v : undefined),
          z.enum(['all', 'slack', 'web']).optional(),
        )
        .default('all'),
      /**
       * Coach turn vs inbox (task-capture) turn. Capture turns are only
       * identified from 2026-08-18; the feature ran without that marker from
       * 2026-08-11, so a capture turn inside that week is counted as Coach.
       * The control's tooltip says so on screen.
       */
      requestType: z
        .preprocess(
          (v) => (v === 'coach' || v === 'inbox' || v === 'all' ? v : undefined),
          z.enum(['all', 'coach', 'inbox']).optional(),
        )
        .default('all'),
      /**
       * Text vs voice. An unrecorded input mode counts as TEXT server-side —
       * an assumption, not a measurement, and the control's tooltip says so.
       */
      inputMode: z
        .preprocess(
          (v) => (v === 'text' || v === 'voice' || v === 'all' ? v : undefined),
          z.enum(['all', 'text', 'voice']).optional(),
        )
        .default('all'),
      /**
       * Whether admin Slack accounts count. `'excluded'` is the default and
       * reproduces every number the dashboard printed before this control
       * existed; `'included'` drops the exclusion so the admins' own rows
       * become visible.
       *
       * TWO values, not three. There is no "admins only" mode — the `users[]`
       * chip already isolates one person by name.
       *
       * SLACK ACCOUNTS, not "admins": the predicate is `slack_id IS NULL OR
       * slack_id NOT IN (admins)` (filters.ts), and a web turn carries no
       * Slack id, so an admin's WEB turns are counted in BOTH states. The
       * control's tooltip says so on screen.
       *
       * Unknown strings coerce to the default, exactly as its three
       * neighbours above do, so a hand-edited URL degrades instead of
       * blanking the whole filter bar.
       */
      admins: z
        .preprocess(
          (v) => (v === 'excluded' || v === 'included' ? v : undefined),
          z.enum(['excluded', 'included']).optional(),
        )
        .default('excluded'),
      granularity: z.enum(['auto', 'hourly', 'daily', 'weekly']).default('auto'),
      /**
       * Day-of-week filter (replaces the old Bug A7 `includeWeekends`
       * boolean). Default `'all'` (2026-06): count every day unless the user
       * narrows to weekdays / weekends. Must stay in lock-step with the
       * backend default in `mapToTraceFilters` (schemas.ts) — both resolve an
       * absent param to 'all', and `stripDefaults` below omits the param when
       * it equals this default. Unknown strings coerce to the default so a
       * hand-edited URL doesn't 400.
       */
      dayFilter: z
        .preprocess(
          (v) => {
            if (v === 'all' || v === 'weekdays' || v === 'weekends') return v;
            return undefined;
          },
          z.enum(['all', 'weekdays', 'weekends']).optional(),
        )
        .default('all'),
      /** Sort state keyed by `tableId` — one map entry per sortable table on
       *  the current page. URL-serialized as `<tableId>_sort=<column>.<dir>`.
       *
       *  Defensive preprocess: some tabs (FeedbackTable's
       *  `use-feedback-url-state`) write a bare `sort=<string>` URL param for
       *  their own sort enum (e.g. `sort=best`). That value lives in the URL
       *  alongside the global filter bar, so when FilterSchema is parsed it
       *  sees `sort: 'best'` — a string, not the expected `{tableId: {column,
       *  dir}}` record. Without this preprocess the whole schema parse fails
       *  and `parseFromSearch` falls back to DEFAULT_FILTERS, poisoning the
       *  filter bar state on that tab (role / channel / preset all reset on
       *  every re-render). Drop anything that isn't an object — the real
       *  table sort state only ever arrives as a record. */
      sort: z
        .preprocess(
          (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : undefined),
          z.record(z.string(), TableSortSchema).optional(),
        ),
    }),
  )
  .transform((parsed) => ({
    ...parsed,
    /**
     * Derived mirror of `dayFilter` kept so existing consumers that still
     * read `filters.includeWeekends` (TrendChart prop, OverviewPage wiring)
     * keep compiling. `true` iff weekends are not excluded — i.e. the
     * `'all'` mode. 'weekdays' and 'weekends' both drop OR gate weekend
     * buckets, so neither should render weekend columns in the chart
     * padder. Read-only: writers must go through `dayFilter`.
     */
    includeWeekends: parsed.dayFilter === 'all',
  }));

export type Filters = z.infer<typeof FilterSchema>;

/** "Empty URL" default matching the empty-filters URL — IA §1.2 default row. */
export const DEFAULT_FILTERS: Filters = {
  preset: '30d',
  role: 'all',
  // BUG E-Cohort: default is no cohort segmentation (absent field).
  cohort: undefined,
  users: [],
  channel: 'any',
  surface: 'all',
  requestType: 'all',
  inputMode: 'all',
  // Admins: excluded by default — the state every number on every tab was
  // computed under before this control existed. Must match the backend
  // default in `filtersQuerySchema` / `mapToTraceFilters`, so an absent param
  // resolves the same way on both sides and `stripDefaults` omits it.
  admins: 'excluded',
  granularity: 'auto',
  // DayFilter: default is 'all' (2026-06) — count every day unless the user
  // narrows. `includeWeekends` is a derived mirror (true here because 'all'
  // keeps Sat/Sun). Must match the backend default in mapToTraceFilters.
  dayFilter: 'all',
  includeWeekends: true,
};

// ---------------------------------------------------------------------------
// Preset → range helpers
// ---------------------------------------------------------------------------

function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function daysAgoIso(days: number, now = new Date()): string {
  const d = new Date(now);
  d.setUTCDate(d.getUTCDate() - days);
  return toIso(d);
}

/** Range span the `all` preset covers — matches the backend's 2020-01-01 floor. */
export const ALL_SINCE_ISO = '2020-01-01';

/**
 * Resolve a preset to concrete ISO dates. Caller is responsible for the
 * `'custom'` branch (there's no preset-to-range answer for custom).
 */
export function presetToRange(
  preset: '24h' | '7d' | '30d' | '90d' | 'all',
  now: Date = new Date(),
): { from: string; to: string } {
  const today = toIso(now);
  if (preset === '24h') return { from: daysAgoIso(1, now), to: today };
  if (preset === '7d') return { from: daysAgoIso(7, now), to: today };
  if (preset === '30d') return { from: daysAgoIso(30, now), to: today };
  if (preset === '90d') return { from: daysAgoIso(90, now), to: today };
  return { from: ALL_SINCE_ISO, to: today };
}

/**
 * Resolve the effective [from, to] window for the given filters.
 *
 * Precedence:
 *   1. `preset === 'custom'` AND both `from`/`to` set → explicit range.
 *   2. Any other preset → `presetToRange(preset)`.
 *   3. Neither preset nor explicit range → fall back to the default preset (30d).
 */
export function resolveRange(
  f: Filters,
  now: Date = new Date(),
): { from: string; to: string } {
  if (f.preset === 'custom' && f.from && f.to) {
    return { from: f.from, to: f.to };
  }
  if (f.preset && f.preset !== 'custom') {
    return presetToRange(f.preset, now);
  }
  if (f.from && f.to) {
    return { from: f.from, to: f.to };
  }
  return presetToRange('30d', now);
}

// ---------------------------------------------------------------------------
// URL serialization helpers
// ---------------------------------------------------------------------------

/**
 * Returns only fields that differ from `DEFAULT_FILTERS`, so the emitted URL
 * stays short. A subsequent call to `FilterSchema.parse({})` on a URL with
 * none of these keys should reproduce `DEFAULT_FILTERS` exactly.
 *
 * - `from` and `to` are only emitted when `preset === 'custom'`.
 * - `users` is dropped when empty.
 * - `sort` is flattened into per-table URL keys (`<tableId>_sort=<col>.<dir>`)
 *   by `serializeSortKeys()`; callers merge those into the URL params.
 */
export function stripDefaults(f: Filters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.preset && f.preset !== DEFAULT_FILTERS.preset) out.preset = f.preset;
  if (f.preset === 'custom') {
    if (f.from) out.from = f.from;
    if (f.to) out.to = f.to;
  }
  if (f.role !== DEFAULT_FILTERS.role) out.role = f.role;
  // BUG E-Cohort: only emit `cohort` when the user has actively picked a
  // tier. `undefined` (the default) means "no segmentation" — absent from
  // the URL.
  if (f.cohort) out.cohort = f.cohort;
  if (f.users.length > 0) out.users = f.users;
  if (f.channel !== DEFAULT_FILTERS.channel) out.channel = f.channel;
  if (f.surface !== DEFAULT_FILTERS.surface) out.surface = f.surface;
  if (f.requestType !== DEFAULT_FILTERS.requestType) {
    out.requestType = f.requestType;
  }
  if (f.inputMode !== DEFAULT_FILTERS.inputMode) out.inputMode = f.inputMode;
  // Admins: emitted ONLY when the user turned the exclusion off, so a shared
  // link that says nothing about admins means the default — and every URL
  // written before this control existed keeps resolving to the numbers it
  // was written about.
  if (f.admins !== DEFAULT_FILTERS.admins) out.admins = f.admins;
  if (f.granularity !== DEFAULT_FILTERS.granularity) {
    out.granularity = f.granularity;
  }
  // DayFilter: only emit when non-default ('all'). Matches the
  // stripDefaults contract for every other enum field — keeps URLs short
  // for the common case. The legacy `includeWeekends` key is deliberately
  // never re-emitted; old bookmarks get folded into dayFilter by the
  // FilterSchema preprocess on the read path.
  if (f.dayFilter !== DEFAULT_FILTERS.dayFilter) {
    out.dayFilter = f.dayFilter;
  }
  // Flatten `sort` → one URL key per table.
  if (f.sort) {
    for (const [tableId, entry] of Object.entries(f.sort)) {
      out[`${tableId}_sort`] = `${entry.column}.${entry.dir}`;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Per-table sort helpers (Plan 03-05 Task 5.1)
// ---------------------------------------------------------------------------

/** URL param suffix used to scope a table's sort state. */
const SORT_URL_SUFFIX = '_sort';

/**
 * The filter projection MINUS the per-table sort keys — i.e. what an answer
 * DEPENDS on, as opposed to how one table on the page is arranged.
 *
 * `stripDefaults` is the URL serialiser, so it has to emit `<tableId>_sort`:
 * that is what makes a sorted table bookmarkable. But those keys were also
 * riding along in every query key and every request URL on the dashboard, and
 * NO endpoint reads them (`grep -rn "_sort" src/dashboard/routes src/services/
 * analytics` finds nothing). One column-header click therefore re-fetched and
 * blanked every widget on the tab — six requests on the user drill-down — for
 * a response that could not differ by a byte.
 *
 * Sorts that the SERVER does apply are passed explicitly (`readTableSort` →
 * `params.sort`) and stay in the key on their own, which is correct: those
 * endpoints sort and then truncate, so a different sort is a different SET of
 * rows, not the same rows reordered (owner's ruling 3, 2026-08-20).
 */
export function filtersQuestion(f: Filters): Record<string, unknown> {
  const out = stripDefaults(f) as Record<string, unknown>;
  for (const key of Object.keys(out)) {
    if (key.endsWith(SORT_URL_SUFFIX)) delete out[key];
  }
  return out;
}

/** Read the sort entry for `tableId`, or `undefined` if unset. */
export function readTableSort(
  filters: Filters,
  tableId: string,
): TableSort | undefined {
  return filters.sort?.[tableId];
}

/**
 * Return a new `Filters` object with `tableId`'s sort set to `next`, or
 * cleared (`undefined` / `null`) so the table falls back to its prop default.
 * Caller is responsible for propagating the result (e.g. via `setPending`).
 */
export function writeTableSort(
  filters: Filters,
  tableId: string,
  next: TableSort | null,
): Filters {
  const current = filters.sort ?? {};
  if (next === null) {
    if (!(tableId in current)) return filters;
    const { [tableId]: _removed, ...rest } = current;
    const sortEmpty = Object.keys(rest).length === 0;
    return { ...filters, sort: sortEmpty ? undefined : rest };
  }
  return { ...filters, sort: { ...current, [tableId]: next } };
}

/**
 * Parse any `*_sort=<col>.<dir>` params out of a raw key/value map and
 * project them into the `sort` shape. Invalid entries are dropped (the URL
 * is user-editable). Returns `undefined` when no entries match.
 */
export function parseSortKeys(
  raw: Record<string, unknown>,
): Record<string, TableSort> | undefined {
  const out: Record<string, TableSort> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!key.endsWith(SORT_URL_SUFFIX)) continue;
    if (typeof value !== 'string') continue;
    const tableId = key.slice(0, -SORT_URL_SUFFIX.length);
    if (!tableId) continue;
    const dot = value.lastIndexOf('.');
    if (dot <= 0 || dot === value.length - 1) continue;
    const column = value.slice(0, dot);
    const dir = value.slice(dot + 1);
    if (dir !== 'asc' && dir !== 'desc') continue;
    out[tableId] = { column, dir };
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
